import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { collection, onSnapshot, getDocs } from 'firebase/firestore';
import { auth } from './auth';
import IssueList from './IssueList';
import logo from './reflection-logo.svg';

export default function ProgressNotesPage() {
  const { issueId } = useParams();
  const [issue, setIssue] = useState(null);
  const [notes, setNotes] = useState([]);
  const [userName, setUserName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const notesPerPage = 20;
  const navigate = useNavigate();
  const [isSCUser, setIsSCUser] = useState(false);
  // SC User check
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if (user && user.email) {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        let found = false;
        usersSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.email && data.email.toLowerCase() === user.email.toLowerCase()) {
            found = true;
          }
        });
        setIsSCUser(found);
      } else {
        setIsSCUser(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Listen for the selected issue
    const unsub = onSnapshot(collection(db, 'issues'), (snapshot) => {
      const found = snapshot.docs.find(doc => doc.id === issueId);
      if (found) setIssue({ ...found.data(), id: found.id });
    });
    return () => unsub();
  }, [issueId]);

  useEffect(() => {
    if (!issueId) return;
    // Listen for notes for this issue
    const unsub = onSnapshot(collection(db, 'issues', issueId, 'notes'), (snapshot) => {
      const notesList = [];
      snapshot.forEach(doc => notesList.push({ ...doc.data(), id: doc.id }));
      setNotes(notesList);
    });
    return () => unsub();
  }, [issueId]);

  // Always show latest note on top by full ISO date+time (dateEntered or createdAt)
  const sortedNotes = [...notes].sort((a, b) => {
    const aDateStr = a.dateEntered || a.createdAt || '';
    const bDateStr = b.dateEntered || b.createdAt || '';
    return bDateStr.localeCompare(aDateStr); // latest first
  });

  // Paging logic
  const totalPages = Math.ceil(sortedNotes.length / notesPerPage);
  const pagedNotes = sortedNotes.slice((currentPage - 1) * notesPerPage, currentPage * notesPerPage);

  useEffect(() => {
    // Get current user's display name only
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        setUserName(user.displayName ? user.displayName : 'Unknown User');
      }
    });
    return () => unsubscribe();
  }, []);

  // File upload logic
  async function handleFileUpload(e) {
    if (!isSCUser) {
      e.preventDefault();
      alert('Only SC Users are allowed!');
      return;
    }
    const file = e.target.files[0];
    if (!file || !issueId || !auth.currentUser) return;
    // Dynamically import Firebase Storage
    const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
    const storage = getStorage();
    const fileRef = ref(storage, `issues/${issueId}/${Date.now()}_${file.name}`);
    try {
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      // Lookup user name from users collection by email
      let uploaderName = '';
      try {
        const userEmail = auth.currentUser.email;
        const usersSnapshot = await getDocs(collection(db, 'users'));
        usersSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.email && data.email.toLowerCase() === userEmail.toLowerCase()) {
            uploaderName = data.name;
          }
        });
        if (!uploaderName) uploaderName = userEmail.split('@')[0];
      } catch (e) {
        uploaderName = 'Unknown User';
      }
      // Add note entry with file link
      const notesCol = collection(db, 'issues', issueId, 'notes');
      const { addDoc } = await import('firebase/firestore');
      await addDoc(notesCol, {
        notes: `<a href='${url}' target='_blank' rel='noopener noreferrer'>${file.name}</a>`,
        byWhom: uploaderName,
        dateEntered: new Date().toISOString(),
        fileUrl: url,
        fileName: file.name,
        createdAt: new Date().toISOString()
      });
      alert('File uploaded and note added!');
    } catch (err) {
      alert('Failed to upload file.');
    }
  }

  async function handleApproval() {
    if (!isSCUser) {
      alert('Only SC Users are allowed!');
      return;
    }
    if (!window.confirm('Are you sure?')) return;
    if (!issueId || !auth.currentUser) return;
    // Lookup user name from users collection by email
    let approvalName = '';
    try {
      const userEmail = auth.currentUser.email;
      const usersSnapshot = await getDocs(collection(db, 'users'));
      usersSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.email && data.email.toLowerCase() === userEmail.toLowerCase()) {
          approvalName = data.name;
        }
      });
      if (!approvalName) approvalName = userEmail.split('@')[0];
    } catch (e) {
      approvalName = 'Unknown User';
    }
    const now = new Date();
    const note = {
      notes: 'This is my approval.',
      byWhom: approvalName,
      dateEntered: now.toISOString(),
    };
    const notesCol = collection(db, 'issues', issueId, 'notes');
    const { addDoc } = await import('firebase/firestore');
    await addDoc(notesCol, note);
  }

  function handleAddNotes(e) {
    if (!isSCUser) {
      if (e) e.preventDefault();
      alert('Only SC Users are allowed!');
      return;
    }
    navigate(`/issues/${issueId}/progress/add`);
  }

  if (!issue) return <div>Loading...</div>;

  return (
    <div style={{ maxWidth: '100%', margin: '0 auto', padding: '0 1rem' }}>
      <div style={{ background: '#183a6d', color: 'white', padding: '1rem', borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center' }}>
        <h2 style={{ margin: 0, color: 'white', fontSize: '1.2rem' }}>Issue ID: {issueId}</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button 
            className="register-btn" 
            style={{ padding: '4px 10px', fontSize: '0.93em', width: 90, minWidth: 70, marginRight: 6, whiteSpace: 'nowrap', lineHeight: '1.1' }} 
            onClick={handleApproval}
          >Approval</button>
          <button 
            className="register-btn" 
            style={{ padding: '4px 10px', fontSize: '0.93em', width: 90, minWidth: 70, marginRight: 6, whiteSpace: 'nowrap', lineHeight: '1.1' }} 
            onClick={handleAddNotes}
          >Add Notes</button>
          <label htmlFor="file-upload" style={{ margin: 0 }}>
            <button 
              className="register-btn" 
              type="button" 
              style={{ padding: '4px 10px', fontSize: '0.93em', width: 90, minWidth: 70, whiteSpace: 'nowrap', lineHeight: '1.1' }} 
              onClick={e => { if (!isSCUser) { e.preventDefault(); alert('Only SC Users are allowed!'); return; } document.getElementById('file-upload').click(); }}
            >Add Files</button>
          </label>
          <input
            id="file-upload"
            type="file"
            accept=".pdf,.doc,.docx,image/*"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
        </div>
      </div>
      <IssueList issues={issue ? [issue] : []} hideActions={true} hideProgressLink={true} />

      <div style={{ marginTop: '2rem' }}>
        <div style={{ background: '#183a6d', color: 'white', padding: '1rem', borderRadius: '8px 8px 0 0' }}>
          <h2 style={{ margin: 0, color: 'white', fontSize: '1.2rem' }}>Notes to this Issue</h2>
        </div>
        <div style={{ background: '#fff', padding: '1rem', borderRadius: '0 0 8px 8px' }}>
          <table>
            <thead>
              <tr>
                <th>Date Entered</th>
                <th>By Whom</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {pagedNotes.map(note => (
                <tr key={note.id}>
                  <td style={{whiteSpace:'nowrap'}}>
                    {note.dateEntered ? (
                      <>
                        {new Date(note.dateEntered).toLocaleDateString()}<br />
                        <span style={{fontSize:'0.95em',color:'#888'}}>{new Date(note.dateEntered).toLocaleTimeString()}</span>
                      </>
                    ) : ''}
                  </td>
                  <td>{note.byWhom}</td>
                  <td dangerouslySetInnerHTML={{ __html: note.notes }}></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="pagination" style={{ marginTop: '1rem', justifyContent: 'center' }}>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Prev</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
